import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { TRAFFIC_FINE_BENEFITS } from '../../components/FeatureComponent/RegisteredPackageInfo'
import { VEHICLE_TYPE } from '../../constants/global'
import { PATH } from '../../constants/router'
import { formatTwoDigitValue, number_to_price } from '../../helper/common'
import userProductService from '../../services/userProductService'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import { checkDurationUsageDate } from '../../helper/formatDate'
import SafeImage from '../../components/BasicComponent/SafeImage'
import { IconGoiDichVuGiaTien, IconGoiDichVuSoLuongXe, IconGoiDichVuSoLuongXeMay } from '../../assets/icons'
import useThemeFeatureConfig from '../../hooks/common/useThemeFeatureConfig'
import Layout1 from './components/Layout1'
import Layout2 from './components/Layout2'
import './index.scss'

const SERVICE_ZALO = 2
const SERVICE_CATEGORY_3 = 3
const SERVICE_CATEGORY_4 = 4
const SERVICE_NOTIFY_CRIMINAL = 9

const SERVICE_PRODUCT_CATEGORIES = [
  SERVICE_ZALO,
  SERVICE_CATEGORY_3,
  SERVICE_CATEGORY_4,
  SERVICE_NOTIFY_CRIMINAL
]
const TRAFFIC_FINE_BENEFITS_MAP = TRAFFIC_FINE_BENEFITS.reduce((result, item) => {
  result[String(item.key)] = item
  return result
}, {})
const DEFAULT_PRODUCT_BENEFITS = [15, 16, 17]
function getProductVehicleType(vehicleType) {
  return vehicleType === null || vehicleType === undefined || vehicleType === '' ? VEHICLE_TYPE.CAR : Number(vehicleType)
}
function isCarProduct(vehicleType) {
  return getProductVehicleType(vehicleType) === VEHICLE_TYPE.CAR
}

function isMotorbikeProduct(vehicleType) {
  return [VEHICLE_TYPE.MOTOBIKE, VEHICLE_TYPE.ELECTRIC_MOTORBIKE].includes(getProductVehicleType(vehicleType))
}

function getProductSortOrder(product) {
  if (isCarProduct(product?.vehicleType)) {
    return 0
  }

  return Number(product?.productSize) > 1 ? 2 : 1
}

function sortProducts(products = []) {
  return [...products].sort((productA, productB) => {
    const sortOrderA = getProductSortOrder(productA)
    const sortOrderB = getProductSortOrder(productB)

    if (sortOrderA !== sortOrderB) {
      return sortOrderA - sortOrderB
    }

    const priorityA = Number(productA?.productPriority) || 0
    const priorityB = Number(productB?.productPriority) || 0

    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    return Number(productA?.productId) - Number(productB?.productId)
  })
}

function filterVisibleProducts(products = []) {
  return products.filter((product) => {
    if (!SERVICE_PRODUCT_CATEGORIES.includes(Number(product?.productCategory))) {
      return false
    }

    if (Number(product?.isDeleted) === 1) {
      return false
    }

    return Number(product?.isVisible ?? 1) === 1
  })
}

function filterProductsByVehicleType(products = [], vehicleType) {
  const targetVehicleType = Number(vehicleType)

  if (!targetVehicleType) {
    return products
  }

  return products.filter((product) => getProductVehicleType(product?.vehicleType) === targetVehicleType)
}

function normalizeBenefitIds(benefits) {
  const normalizedBenefits = Array.isArray(benefits) ? benefits.map(Number).filter((item) => !Number.isNaN(item)) : []
  return normalizedBenefits.length ? normalizedBenefits : DEFAULT_PRODUCT_BENEFITS
}

function getBenefits(productOptionalInfo) {
  if (!productOptionalInfo) {
    return DEFAULT_PRODUCT_BENEFITS
  }

  if (typeof productOptionalInfo === 'object' && !Array.isArray(productOptionalInfo)) {
    return normalizeBenefitIds(productOptionalInfo?.benefits)
  }

  try {
    const parsedInfo = JSON.parse(productOptionalInfo)
    return normalizeBenefitIds(parsedInfo?.benefits)
  } catch {
    return DEFAULT_PRODUCT_BENEFITS
  }
}

function isSameVehicleGroup(sourceVehicleType, targetVehicleType) {
  if (isCarProduct(sourceVehicleType)) {
    return isCarProduct(targetVehicleType)
  }

  if (isMotorbikeProduct(sourceVehicleType)) {
    return isMotorbikeProduct(targetVehicleType)
  }

  return Number(sourceVehicleType) === Number(targetVehicleType)
}

function getDefaultSelectedProduct(products = [], preferredVehicleType) {
  if (!products.length) {
    return null
  }

  if (preferredVehicleType) {
    const exactMatchedProduct = products.find((product) => getProductVehicleType(product?.vehicleType) === Number(preferredVehicleType))

    if (exactMatchedProduct) {
      return exactMatchedProduct
    }

    const matchedProduct = products.find((product) => isSameVehicleGroup(product?.vehicleType, preferredVehicleType))

    if (matchedProduct) {
      return matchedProduct
    }
  }

  return products[0]
}
function getVehicleLabel(vehicleType) {
  const normalizedVehicleType = getProductVehicleType(vehicleType)

  if (normalizedVehicleType === VEHICLE_TYPE.CAR) {
    return 'ô tô'
  }

  if (normalizedVehicleType === VEHICLE_TYPE.ELECTRIC_MOTORBIKE) {
    return 'xe máy điện'
  }

  return 'xe máy'
}

function getVehicleCountText(product) {
  const vehicleCount = Number(product?.productSize) || 1
  const vehicleLabel = getVehicleLabel(product?.vehicleType)

  return `${formatTwoDigitValue(vehicleCount)} ${vehicleLabel}`
} 
function getProductPriceText(product) {
  const productPrice = product?.productPriceAfterVAT ?? product?.productPrice ?? 0
  const durationText = product?.usageDate ? checkDurationUsageDate(product.usageDate, false) : ''

  return `${number_to_price(productPrice)}đ${durationText}`
}

function getProductBenefitItems(benefitIds = []) {
  return benefitIds.reduce((result, benefitId) => {
    const benefit = TRAFFIC_FINE_BENEFITS_MAP[String(benefitId)]
    const benefitIcon = benefit?.icon2 || benefit?.icon
    const benefitText = benefit?.text2 || benefit?.text

    if (!benefitText) {
      return result
    }

    const BenefitIconComponent = benefitIcon

    result.push({
      key: `benefit-${benefit.key}`,
      icon: benefitIcon ? (
        typeof benefitIcon === 'string' ? (
          <SafeImage src={benefitIcon} aria-hidden="true" />
        ) : (
          <BenefitIconComponent aria-hidden="true" />
        )
      ) : null,
      label: benefitText,
      isActive: true
    })

    return result
  }, [])
}

export default function TrafficFineNotificationServices() {
  const history = useHistory()
  const location = useLocation()
  const locationState = location.state || {}
  const preferredVehicleIdentity = locationState?.trafficFineRegistrationVehicle?.vehicleIdentity || ''
  const preferredVehicleType = locationState?.trafficFineRegistrationVehicle?.vehicleType
    ? Number(locationState.trafficFineRegistrationVehicle.vehicleType)
    : undefined
  const preferredProductVehicleType = preferredVehicleType
  const preferredProductId = locationState?.trafficFineRegistrationProduct?.productId

  const [products, setProducts] = useState([])
  const [selectedProductId, setSelectedProductId] = useState(null)
  const [isLoadingProducts, setIsLoadingProducts] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const { layoutVersion } = useThemeFeatureConfig('TRAFFIC_FINE_NOTIFICATION_SERVICES')

  const handleRetryLoadProducts = useCallback(() => {
    setReloadTick((tick) => tick + 1)
  }, [])

  useEffect(() => {
    let isMounted = true

    async function fetchProducts() {
      try {
        setIsLoadingProducts(true)
        setHasLoadError(false)

        const response = await userProductService.PartnerAPIProductUserGetList({
          filter: {
            productCategory: SERVICE_PRODUCT_CATEGORIES
          }
        })

        if (!isMounted) {
          return
        }

        if (response?.statusCode !== 200) {
          setProducts([])
          setHasLoadError(true)
          return
        }

        const productList = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : []

        const visibleProducts = filterVisibleProducts(productList)
        const vehicleTypeProducts = preferredVehicleIdentity
          ? visibleProducts
          : filterProductsByVehicleType(visibleProducts, preferredVehicleType)
        const nextProducts = sortProducts(vehicleTypeProducts)

        setProducts(nextProducts)
        setHasLoadError(false)
      } catch {
        if (!isMounted) {
          return
        }

        setProducts([])
        setHasLoadError(true)
      } finally {
        if (isMounted) {
          setIsLoadingProducts(false)
        }
      }
    }

    fetchProducts()

    return () => {
      isMounted = false
    }
  }, [reloadTick, preferredVehicleType])

  useEffect(() => {
    if (!products.length) {
      setSelectedProductId(null)
      return
    }

    const selectedProductExists = products.some((product) => Number(product?.productId) === Number(selectedProductId))

    if (selectedProductExists) {
      return
    }

    const preferredProduct = products.find((product) => Number(product?.productId) === Number(preferredProductId))
    const defaultProduct = preferredProduct || getDefaultSelectedProduct(products, preferredProductVehicleType)
    setSelectedProductId(defaultProduct?.productId ?? null)
  }, [preferredProductId, preferredProductVehicleType, products, selectedProductId])

  const selectedProduct = useMemo(() => {
    return products.find((product) => Number(product?.productId) === Number(selectedProductId)) || null
  }, [products, selectedProductId])

  const selectedBenefits = useMemo(() => getBenefits(selectedProduct?.productOptionalInfo), [selectedProduct])

  const productDetailItems = useMemo(() => {
    if (!selectedProduct) {
      return []
    }

    return [
      {
        key: 'price',
        icon: <IconGoiDichVuGiaTien />,
        label: 'Giá gói/tháng',
        value: getProductPriceText(selectedProduct),
        subValue: 'Tự động gia hạn hàng tháng'
      },
      {
        key: 'vehicle-count',
        icon: isMotorbikeProduct(selectedProduct?.vehicleType) ? <IconGoiDichVuSoLuongXeMay /> : <IconGoiDichVuSoLuongXe />,
        label: 'Số lượng xe',
        value: getVehicleCountText(selectedProduct)
      },
      ...getProductBenefitItems(selectedBenefits)
    ]
  }, [selectedBenefits, selectedProduct])

  const handleNextStep = () => {
    if (!selectedProduct) {
      return
    }

    const trafficFineRegistrationVehicle = {
      vehicleIdentity: preferredVehicleIdentity || undefined,
      vehicleType: preferredVehicleType
    }
    const nextLocationState = {
      ...locationState,
      trafficFineRegistrationVehicle,
      trafficFineRegistrationProduct: selectedProduct
    }

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: nextLocationState
    })

    history.push({
      pathname: PATH.TRAFFIC_FINE_VEHICLES,
      state: {
        trafficFineRegistrationVehicle,
        trafficFineRegistrationProduct: selectedProduct
      }
    })
  }

  const renderLayout = () => {
    if (layoutVersion === 2) {
      return (
        <Layout2
          products={products}
          selectedProductId={selectedProductId}
          setSelectedProductId={setSelectedProductId}
          isLoadingProducts={isLoadingProducts}
          hasLoadError={hasLoadError}
          handleRetryLoadProducts={handleRetryLoadProducts}
          handleNextStep={handleNextStep}
          getProductBenefitItems={getProductBenefitItems}
          getBenefits={getBenefits}
          getProductPriceText={getProductPriceText}
          getVehicleCountText={getVehicleCountText}
          getProductVehicleType={getProductVehicleType}
          isMotorbikeProduct={isMotorbikeProduct}
        />
      )
    }

    return (
      <Layout1
        products={products}
        selectedProduct={selectedProduct}
        selectedProductId={selectedProductId}
        setSelectedProductId={setSelectedProductId}
        isLoadingProducts={isLoadingProducts}
        hasLoadError={hasLoadError}
        productDetailItems={productDetailItems}
        handleRetryLoadProducts={handleRetryLoadProducts}
        handleNextStep={handleNextStep}
      />
    )
  }

  return (
    <LayoutPartner headerTitle="Các gói dịch vụ">
      {renderLayout()}
    </LayoutPartner>
  )
}
